/**
 * GLAMRDiP Sticky ATC — PDP scroll-triggered add-to-cart bar.
 *
 * Slides in (bottom on mobile, top on desktop ≥1024) when the page's primary
 * ATC scrolls out of viewport; slides out when it's back. CTA delegates to:
 *   - kit/bundle PDPs → click the existing [data-gd-open-bundle-builder]
 *     element (opens the bundle builder modal with full configuration UX);
 *   - standalone PDPs → submit the page's main product form (which fires
 *     the global AJAX intercept in theme.liquid → adds + opens cart drawer).
 *
 * The IntersectionObserver targets [data-main-atc-target] (an ancestor or
 * the button itself); falls back to a union of the known PDP ATC selectors
 * so sections that haven't opted in still get the bar.
 *
 * Page-speed: zero work until the user hits the PDP. No initial paint
 * impact — the snippet is hidden=true and aria-hidden=true on render. The
 * IntersectionObserver fires on first scroll and toggles classes only.
 */
(() => {
  'use strict';

  const ATC_FALLBACK_SEL = [
    '[data-main-atc-target]',
    '.gd-colour-pdp__atc',
    '.gd-liquid-pdp__atc',
    '.gd-accessory-pdp__atc',
    '[data-gd-open-bundle-builder]',
  ].join(', ');

  class GDStickyATC {
    constructor(stickyEl, mainAtcEl, alwaysVisible) {
      this.sticky = stickyEl;
      this.mainAtc = mainAtcEl;
      this.cta = stickyEl.querySelector('[data-sticky-atc-cta]');
      this.isKit = stickyEl.dataset.isKit === 'true';
      this.alwaysVisible = !!alwaysVisible;
      this._visible = false;
      this._bind();
      // Joel 2026-05-02 (colour PDP #4): always-visible mode skips the
      // IntersectionObserver. The sticky bar is the only ATC on the
      // page (no inline ATC to observe). Force-show on load and stay
      // visible through scroll. The hide() / show() methods still
      // work but nothing calls them in this mode.
      if (this.alwaysVisible) {
        this.show();
      } else {
        this._observe();
      }
    }

    _observe() {
      // IntersectionObserver fires `!isIntersecting` for two distinct
      // states — main ATC scrolled ABOVE the viewport (we want sticky
      // visible), and main ATC sitting BELOW the viewport (initial load
      // on a tall PDP — we DON'T want sticky visible). The earlier build
      // collapsed both into show(); on long PDPs that meant a flash of
      // sticky on page load before the user had even scrolled. Joel
      // 2026-04-28 fix: disambiguate via getBoundingClientRect — only
      // show when the ATC's bottom edge is past the viewport top.
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hide();
            return;
          }
          const rect = this.mainAtc.getBoundingClientRect();
          // rect.bottom < 0 → ATC fully above viewport (scrolled past) → SHOW
          // rect.top > viewport → ATC below viewport (not yet scrolled to) → HIDE
          if (rect.bottom < 0) this.show();
          else this.hide();
        });
      }, { threshold: 0, rootMargin: '0px 0px -80px 0px' });
      obs.observe(this.mainAtc);
    }

    show() {
      if (this._visible) return;
      this._visible = true;
      this.sticky.hidden = false;
      this.sticky.setAttribute('aria-hidden', 'false');
      // rAF so the [hidden] removal flushes before we add the slide-in class
      // — otherwise the transform: translateY(...) → 0 would not animate
      // (initial frame would already be at 0).
      requestAnimationFrame(() => {
        this.sticky.classList.add('is-visible');
      });
    }

    hide() {
      if (!this._visible) return;
      this._visible = false;
      this.sticky.classList.remove('is-visible');
      this.sticky.setAttribute('aria-hidden', 'true');
      // Re-set hidden after the slide-out transition finishes so screen
      // readers stop announcing it. Match the CSS transition duration.
      setTimeout(() => {
        if (!this._visible) this.sticky.hidden = true;
      }, 320);
    }

    _bind() {
      // Joel 2026-04-30: tapping the product info zone (thumbnail + title +
      // price) scrolls the page back to the top. CTA on the right has its
      // own click handler with stopPropagation so the two zones don't
      // overlap.
      const scrollTopBtn = this.sticky.querySelector('[data-sticky-atc-scroll-top]');
      if (scrollTopBtn) {
        scrollTopBtn.addEventListener('click', () => {
          const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
        });
      }

      if (!this.cta) return;
      this.cta.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.cta.disabled) return;
        if (this.isKit) {
          // Defer to the existing bundle-builder open trigger — it knows
          // how to scope its modal to the current product (kit-hero-v2.liquid
          // wires it up via [data-gd-open-bundle-builder]).
          const trigger = document.querySelector('[data-gd-open-bundle-builder]');
          if (trigger) {
            trigger.click();
          }
          return;
        }
        // Standalone product → fire the page's main product form. The
        // mainAtc may BE the form, or its submit button, or live inside one.
        let form = null;
        if (this.mainAtc.tagName === 'FORM') form = this.mainAtc;
        else form = this.mainAtc.closest('form');
        if (!form) return;
        // Prefer clicking the actual submit button (so any button-attached
        // listeners — e.g. analytics — fire too).
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitBtn) submitBtn.click();
        else if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    }
  }

  function init() {
    // Guard so a missing element / unexpected throw doesn't break the page
    // (especially Shopify theme editor — unhandled exceptions surface as
    // "Page failed to load due to an error").
    try {
      const sticky = document.querySelector('[data-sticky-atc]');
      if (!sticky) return;
      if (sticky.dataset.gdStickyInit === '1') return;
      sticky.dataset.gdStickyInit = '1';
      const alwaysVisible = sticky.dataset.alwaysVisible === 'true';
      const mainAtc = document.querySelector(ATC_FALLBACK_SEL);
      // Joel 2026-05-02 (colour PDP #4): in always-visible mode the
      // sticky bar is the page's only ATC — no inline ATC to observe.
      // Pair the bar with the page's product form so CTA-click still
      // submits cleanly (form lookup falls back to any [action="/cart/add"]).
      const mainAtcOrForm = mainAtc
        || (alwaysVisible ? document.querySelector('form[action$="/cart/add"]') : null);
      if (!mainAtcOrForm) return;
      new GDStickyATC(sticky, mainAtcOrForm, alwaysVisible);
    } catch (e) {
      console.error('[gd-sticky-atc] init failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // Re-init on theme-editor section reload (e.g. customizer drag-and-drop).
  document.addEventListener('shopify:section:load', () => init());
})();
